import { styled, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import type {
  Layout,
  LayoutGridComponents,
  LayoutGridKey,
  LayoutWithPosition,
  Layouts,
} from './types';

interface LayoutGridProps<T extends string> {
  initialLayouts: Layouts<T>;
  componentList: LayoutGridComponents<T>;
}

interface GridContainerProps {
  $marginX: number;
  $marginY: number;
  $totalRows: number;
}

interface GridItemProps {
  $colStart: number;
  $colSpan: number;
  $rowStart: number;
  $rowSpan: number;
}

const GridContainer = styled('div')<GridContainerProps>(
  ({ $marginX, $marginY, $totalRows }) => ({
    display: 'grid',
    width: '100%',
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridTemplateRows: `repeat(${$totalRows}, 47px)`,
    gap: `${$marginY}px ${$marginX}px`,
  })
);

const GridItem = styled('div')<GridItemProps>(
  ({ $colStart, $colSpan, $rowStart, $rowSpan }) => ({
    gridColumn: `${$colStart} / span ${$colSpan}`,
    gridRow: `${$rowStart} / span ${$rowSpan}`,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      flex: 1,
      minHeight: 0,
      width: '100%',
    },
  })
);

// Calculate X and Y positions for all widgets automatically
// Algorithm: For each widget, find the first available position where it fits
function calculateLayoutPositions<T>(
  layout: Layout<T>[]
): LayoutWithPosition<T>[] {
  if (!layout || layout.length === 0) return [];

  // Track which cells are occupied using a grid
  // grid[row][col] = true if occupied, false if free
  const grid: boolean[][] = [];

  // Helper function to check if a position is available
  const canFitAt = (x: number, y: number, w: number, h: number): boolean => {
    // Check if it fits within the 12-column grid
    if (x + w > 12) return false;

    // Check if all required cells are free
    for (let row = y; row < y + h; row++) {
      if (!grid[row]) continue; // Row doesn't exist yet, so it's free
      for (let col = x; col < x + w; col++) {
        if (grid[row][col]) return false; // Cell is occupied
      }
    }
    return true;
  };

  // Helper function to mark cells as occupied
  const occupy = (x: number, y: number, w: number, h: number): void => {
    for (let row = y; row < y + h; row++) {
      if (!grid[row]) {
        grid[row] = new Array(12).fill(false);
      }
      for (let col = x; col < x + w; col++) {
        grid[row][col] = true;
      }
    }
  };

  return layout.map((item) => {
    // Find the first available position (scanning row by row, left to right)
    let foundX = 0;
    let foundY = 0;
    let found = false;

    // Start scanning from row 0
    for (let y = 0; !found; y++) {
      for (let x = 0; x <= 12 - item.w; x++) {
        if (canFitAt(x, y, item.w, item.h)) {
          foundX = x;
          foundY = y;
          found = true;
          break;
        }
      }
    }

    // Mark the cells as occupied
    occupy(foundX, foundY, item.w, item.h);

    // Return the widget with calculated X and Y positions
    return {
      ...item,
      x: foundX,
      y: foundY,
    };
  });
}

// Calculate the total number of rows needed for a layout
function calculateTotalRows<T>(layout: LayoutWithPosition<T>[]): number {
  if (!layout || layout.length === 0) return 0;

  let maxRow = 0;
  layout.forEach((item) => {
    const itemEnd = item.y + item.h;
    if (itemEnd > maxRow) {
      maxRow = itemEnd;
    }
  });

  return Math.ceil(maxRow);
}

// Get grid positioning for an item
function getGridPosition<T>(item: LayoutWithPosition<T>) {
  // CSS Grid is 1-indexed
  const colStart = item.x + 1;
  const colSpan = item.w;
  const rowStart = item.y + 1;
  const rowSpan = item.h;

  return { colStart, colSpan, rowStart, rowSpan };
}

// Hook to detect current breakpoint based on media queries
const useCurrentBreakpoint = (): LayoutGridKey => {
  const theme = useTheme();

  // Define breakpoint queries matching modern device standards
  // xxs: 0-480px (small phones), xs: 480-768px (large phones/small tablets),
  // sm: 768-1024px (tablets), md: 1024-1440px (small desktops/laptops), lg: 1440px+ (large desktops)
  const isXs = useMediaQuery(theme.breakpoints.between(480, 768)); // 480-768px
  const isSm = useMediaQuery(theme.breakpoints.between(768, 1024)); // 768-1024px
  const isMd = useMediaQuery(theme.breakpoints.between(1024, 1440)); // 1024-1440px
  const isLg = useMediaQuery(theme.breakpoints.up(1440)); // >= 1440px

  // Return the current breakpoint key
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  if (isXs) return 'xs';
  return 'xxs';
};

export default function LayoutGrid<T extends string>({
  initialLayouts,
  componentList,
}: LayoutGridProps<T>) {
  const theme = useTheme();

  // Detect current breakpoint
  const currentBreakpoint = useCurrentBreakpoint();

  // Memoize items to prevent recalculation
  const items = useMemo(() => Object.keys(componentList), [componentList]);

  const [layouts, setLayouts] = useState<Layouts<T>>(initialLayouts);

  // Update layouts when initialLayouts prop changes (e.g., when switching templates)
  useEffect(() => {
    setLayouts(initialLayouts);
  }, [initialLayouts]);

  const marginX = theme.tokens.spacing.md;
  const marginY = theme.tokens.spacing.lg;

  // Get current breakpoint layout with fallback to next available breakpoint
  // and calculate Y positions automatically
  const currentLayout = useMemo(() => {
    // Try to get layout for current breakpoint
    let baseLayout: Layout<T>[] | undefined = layouts[currentBreakpoint];

    if (!baseLayout) {
      // Fallback order based on current breakpoint
      const fallbackOrder: Record<LayoutGridKey, LayoutGridKey[]> = {
        lg: ['md', 'sm', 'xs', 'xxs'],
        md: ['sm', 'lg', 'xs', 'xxs'],
        sm: ['md', 'xs', 'lg', 'xxs'],
        xs: ['xxs', 'sm', 'md', 'lg'],
        xxs: ['xs', 'sm', 'md', 'lg'],
      };

      // Find first available layout from fallback order
      for (const breakpoint of fallbackOrder[currentBreakpoint]) {
        if (layouts[breakpoint]) {
          baseLayout = layouts[breakpoint];
          break;
        }
      }

      // Last resort: return any available layout
      if (!baseLayout) {
        baseLayout =
          layouts.lg ||
          layouts.md ||
          layouts.sm ||
          layouts.xs ||
          layouts.xxs ||
          [];
      }
    }

    // Calculate Y positions automatically
    return calculateLayoutPositions(baseLayout);
  }, [layouts, currentBreakpoint]);

  // Memoize totalRows calculation to prevent layout shifts during render
  const totalRows = useMemo(
    () => calculateTotalRows(currentLayout),
    [currentLayout]
  );

  return (
    <GridContainer $marginX={marginX} $marginY={marginY} $totalRows={totalRows}>
      {items.map((key) => {
        const layoutItem = currentLayout.find((l) => l.i === key);

        if (!layoutItem) {
          return null;
        }

        const { colStart, colSpan, rowStart, rowSpan } =
          getGridPosition(layoutItem);

        return (
          <GridItem
            key={key}
            $colStart={colStart}
            $colSpan={colSpan}
            $rowStart={rowStart}
            $rowSpan={rowSpan}
          >
            {componentList[key].component}
          </GridItem>
        );
      })}
    </GridContainer>
  );
}
